import React, { useEffect, useMemo, useState } from 'react'
import PhoneInput from 'react-phone-number-input'
import { useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, Check, Lock } from 'tabler-icons-react'
import {
	createExperienceStoreCheckout,
	getExperienceStore,
} from '../services/actions/experienceStoreActions'
import { getContrastTextColor } from './myExperienceUtils'
import './ExperienceStorePage.css'

const formatMoney = (amountCents, currency = 'usd') =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: String(currency || 'usd').toUpperCase(),
	}).format(Number(amountCents || 0) / 100)

const formatDateRange = (startDate, endDate) => {
	if (!startDate) return ''
	const formatter = new Intl.DateTimeFormat('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'UTC',
	})
	const start = formatter.format(new Date(startDate))
	if (
		!endDate ||
		String(endDate).slice(0, 10) === String(startDate).slice(0, 10)
	) {
		return start
	}
	return `${start} – ${formatter.format(new Date(endDate))}`
}

const ExperienceStorePage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const [searchParams] = useSearchParams()
	const [store, setStore] = useState(null)
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState('')
	const [selectedItemId, setSelectedItemId] = useState('')
	const [travelerName, setTravelerName] = useState('')
	const [travelerEmail, setTravelerEmail] = useState('')
	const [travelerPhone, setTravelerPhone] = useState('')
	const [accountName, setAccountName] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [checkoutError, setCheckoutError] = useState('')

	useEffect(() => {
		const controller = new AbortController()
		let active = true
		setLoading(true)
		setLoadError('')
		getExperienceStore(orgSlug, experienceSlug, controller.signal)
			.then((result) => {
				if (active) setStore(result)
			})
			.catch((error) => {
				if (active && error.name !== 'AbortError') {
					setLoadError(error.message || 'This store is not available.')
				}
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
			controller.abort()
		}
	}, [experienceSlug, orgSlug])

	const selectedItem = useMemo(
		() => store?.items?.find((item) => item.id === selectedItemId) || null,
		[selectedItemId, store?.items],
	)
	const experience = store?.experience || {}
	const accentColor = experience.primaryColor || '#176b5b'
	const accentTextColor = getContrastTextColor(accentColor)

	const selectItem = (item) => {
		if (item.inventory?.isSoldOut) return
		const isDeselecting = selectedItemId === item.id
		setSelectedItemId(isDeselecting ? '' : item.id)
		setCheckoutError('')
		if (isDeselecting) return
		window.requestAnimationFrame(() => {
			document
				.getElementById('traveler-details')
				?.scrollIntoView({ behavior: 'smooth', block: 'start' })
		})
	}

	const submitCheckout = async (event) => {
		event.preventDefault()
		if (!selectedItem || !travelerPhone || !accountName.trim()) {
			setCheckoutError(
				'Select an item and complete your check-in details to continue.',
			)
			return
		}

		setSubmitting(true)
		setCheckoutError('')
		try {
			const result = await createExperienceStoreCheckout(
				orgSlug,
				experienceSlug,
				{
					itemOptionId: selectedItem.id,
					travelerName,
					travelerEmail,
					travelerPhone,
					accountName,
				},
			)
			const checkoutUrl = result?.checkoutSession?.checkoutUrl
			if (!checkoutUrl)
				throw new Error('Checkout did not return a payment link.')
			window.location.assign(checkoutUrl)
		} catch (error) {
			setCheckoutError(error.message || 'Checkout could not be started.')
			setSubmitting(false)
		}
	}

	if (loading) {
		return (
			<div className='experience-store-status-page'>
				<div className='experience-store-spinner' aria-label='Loading store' />
				<p>Loading store…</p>
			</div>
		)
	}

	if (loadError || !store) {
		return (
			<div className='experience-store-status-page'>
				<AlertCircle size={34} />
				<h1>Store unavailable</h1>
				<p>{loadError || 'This experience store is not available.'}</p>
			</div>
		)
	}

	return (
		<div
			className='experience-store-page'
			style={{
				'--store-accent': accentColor,
				'--store-accent-text': accentTextColor,
			}}
		>
			<div className='experience-store-shell'>
				<header
					className='experience-store-hero'
					style={{
						...(experience.headerImage
							? {
									backgroundImage: `url("${experience.headerImage.replace(/"/g, '%22')}")`,
								}
							: {}),
					}}
				>
					<div className='experience-store-hero-overlay' />
					<div className='experience-store-topbar'>
						<div className='experience-store-brand'>
							{store.org?.logo ? (
								<img src={store.org.logo} alt={`${store.org.name} logo`} />
							) : (
								<span>LH</span>
							)}
						</div>
						<p>EXPERIENCE STORE</p>
						<div aria-hidden='true' />
					</div>

					<div className='experience-store-hero-content'>
						<div>
							<h1>{experience.shortName || experience.name}</h1>
							{experience.location?.name ? (
								<p className='experience-store-location'>
									{experience.location.name}
								</p>
							) : null}
							{formatDateRange(experience.startDate, experience.endDate) ? (
								<p className='experience-store-dates'>
									{formatDateRange(experience.startDate, experience.endDate)}
								</p>
							) : null}
						</div>
					</div>
				</header>

				<div className='experience-store-content'>
					<h2 className='experience-store-page-title'>Experience Store</h2>
					{searchParams.get('checkout') === 'canceled' ? (
						<div className='experience-store-notice' role='status'>
							Payment was canceled. Nothing was charged; you can try again
							below.
						</div>
					) : null}

					<section aria-labelledby='store-items-heading'>
						<div className='experience-store-section-heading'>
							<div>
								<p className='experience-store-step'>Step 1</p>
								<h2 id='store-items-heading'>Choose your item</h2>
							</div>
						</div>

						{store.items?.length ? (
							<div className='experience-store-grid'>
								{store.items.map((item) => {
									const selected = item.id === selectedItemId
									const soldOut = item.inventory?.isSoldOut
									return (
										<article
											key={item.id}
											className={`experience-store-card${selected ? ' selected' : ''}`}
										>
											{item.imageUrl ? (
												<img src={item.imageUrl} alt='' />
											) : null}
											<div className='experience-store-card-body'>
												<div className='experience-store-card-title-row'>
													<h3>{item.name}</h3>
													<strong>
														{formatMoney(item.totalAmountCents, item.currency)}
													</strong>
												</div>
												{item.description ? <p>{item.description}</p> : null}
												{item.customerFeeAmountCents > 0 ? (
													<small>
														Includes a{' '}
														{formatMoney(
															item.customerFeeAmountCents,
															item.currency,
														)}{' '}
														checkout fee
													</small>
												) : null}
												<button
													type='button'
													onClick={() => selectItem(item)}
													disabled={soldOut}
													aria-pressed={selected}
												>
													{soldOut ? (
														'Sold out'
													) : selected ? (
														<>
															<Check size={17} /> Selected
														</>
													) : (
														'Select'
													)}
												</button>
											</div>
										</article>
									)
								})}
							</div>
						) : (
							<div className='experience-store-empty'>
								No items are available right now.
							</div>
						)}
					</section>

					<section
						id='traveler-details'
						className={`experience-store-checkout${selectedItem ? ' visible' : ''}`}
						aria-labelledby='traveler-details-heading'
					>
						<div className='experience-store-section-heading'>
							<div>
								<p className='experience-store-step'>Step 2</p>
								<h2 id='traveler-details-heading'>Your check-in details</h2>
							</div>
						</div>

						<form onSubmit={submitCheckout}>
							<div className='experience-store-form-fields'>
								<label>
									<span>Full Name</span>
									<input
										value={travelerName}
										onChange={(event) => setTravelerName(event.target.value)}
										autoComplete='name'
										maxLength={120}
										required
									/>
								</label>
								<label>
									<span>Email</span>
									<input
										type='email'
										value={travelerEmail}
										onChange={(event) => setTravelerEmail(event.target.value)}
										autoComplete='email'
										maxLength={254}
										required
									/>
								</label>
								<label>
									<span>University / School</span>
									<input
										value={accountName}
										onChange={(event) => setAccountName(event.target.value)}
										autoComplete='organization'
										maxLength={120}
										required
									/>
								</label>
								<label>
									<span>Phone</span>
									<PhoneInput
										className='experience-store-phone-input'
										defaultCountry='US'
										value={travelerPhone}
										onChange={(value) => setTravelerPhone(value || '')}
										international
										countryCallingCodeEditable={false}
									/>
								</label>
							</div>

							{checkoutError ? (
								<div className='experience-store-form-error' role='alert'>
									<AlertCircle size={18} /> {checkoutError}
								</div>
							) : null}

							<div className='experience-store-order-bar'>
								<div>
									<span>{selectedItem?.name || 'Select an item'}</span>
									<strong>
										{selectedItem
											? formatMoney(
													selectedItem.totalAmountCents,
													selectedItem.currency,
												)
											: '—'}
									</strong>
								</div>
								<button type='submit' disabled={!selectedItem || submitting}>
									{!submitting ? <Lock size={17} /> : null}
									{submitting
										? 'Opening secure checkout…'
										: 'Continue to payment'}
								</button>
							</div>
						</form>
					</section>
				</div>
				<footer className='experience-store-footer'>
					Secure checkout powered by Stripe
				</footer>
			</div>
		</div>
	)
}

export default ExperienceStorePage
